import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  ArrowLeftOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BellIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import { useOwnerLayout } from '../use-cases/useOwnerLayout';
import NotificationDropdown from './NotificationDropdown';
import UpdateNoticeModal from './UpdateNoticeModal';
import TodayNoticeBanner from './TodayNoticeBanner';
import SubscriptionConsentModal from './SubscriptionConsentModal';
import ChatWidget from '../../../components/chatbot/ChatWidget';

/** 묶음 안 메뉴 숫자의 합 — 접힌 묶음 제목 옆에 띄운다 */
const categoryBadge = (category: { items: { badge?: number | string }[] }): number =>
  category.items.reduce((sum, it) => sum + (Number(it.badge) || 0), 0);

const OwnerLayoutV2: React.FC = () => {
  const location = useLocation();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const {
    expandedCategories,
    isSidebarOpen,
    setIsSidebarOpen,
    isDesktopSidebarCollapsed,
    toggleDesktopSidebar,
    totalNotifications,
    organizationName,
    navigationCategories,
    toggleCategory,
    handleLogout,
  } = useOwnerLayout();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* 업데이트 공지 모달 */}
      <UpdateNoticeModal />
      {/* 새 이용약관·요금 동의 창 (2026-10-27) — 동의 전 원장에게만, 서버가 띄울지 정한다 */}
      <SubscriptionConsentModal />

      <div className="min-h-screen bg-gray-50 flex relative">
        {/* 모바일 햄버거 메뉴 */}
        <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-30">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
              <span className="ml-2 font-bold text-gray-900">
                {organizationName}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <button
                  onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                  className="relative p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  <BellIcon className="h-6 w-6" />
                  {totalNotifications > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {totalNotifications > 9 ? '9+' : totalNotifications}
                    </span>
                  )}
                </button>
                <NotificationDropdown
                  isOpen={isNotificationOpen}
                  onClose={() => setIsNotificationOpen(false)}
                  unreadCount={totalNotifications}
                />
              </div>
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* 모바일 사이드바 */}
        {isSidebarOpen && (
          <>
            {/* 배경 오버레이 */}
            <div
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsSidebarOpen(false)}
            />

            {/* 모바일 사이드바 */}
            <div className="lg:hidden fixed inset-y-0 left-0 w-80 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
              {/* 헤더 - 고정 */}
              <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                  <span className="ml-2 font-bold text-gray-900">
                    {organizationName}
                  </span>
                </div>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* 알림 센터 - 고정 */}
              <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
                <Link
                  to="/owner/notifications"
                  onClick={() => setIsSidebarOpen(false)}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <BellIcon className="h-5 w-5 text-gray-600" />
                    <span className="ml-2 text-sm text-gray-700">알림</span>
                  </div>
                  {totalNotifications > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                      {totalNotifications > 99 ? '99+' : totalNotifications}
                    </span>
                  )}
                </Link>
              </div>

              {/* 네비게이션 메뉴 - 스크롤 가능 */}
              <nav
                className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                style={{ maxHeight: 'calc(100vh - 250px)' }}
              >
                {navigationCategories.map((category) => {
                  const isExpanded = expandedCategories.includes(category.title);
                  return (
                    <div key={category.title} className="mb-4">
                      <button
                        onClick={() => toggleCategory(category.title)}
                        className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                      >
                        <div className="flex items-center">
                          <category.icon className="h-5 w-5 mr-2 text-gray-500" />
                          {category.title}
                        </div>
                        <span className="flex items-center gap-1.5">
                          {/* 🔴 2026-09-24 — 접힌 묶음 안의 숫자가 안 보였다(우장산 '승인 대기가 있는데 숫자가 없다').
                                 사이드바는 '운영 관리'만 펼친 채로 열려, 사유서·학부모 소통 숫자가 묶음 속에 숨어 있었다. */}
                          {!isExpanded && categoryBadge(category) > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{categoryBadge(category)}</span>
                          )}
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                          )}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="mt-1 space-y-1 pl-7">
                          {category.items.map((item) => {
                            const isCurrentPath = isActive(item.href);

                            if (item.comingSoon) {
                              return (
                                <button
                                  key={item.name}
                                  onClick={() => alert('🚧 준비중입니다\n\n이 기능은 현재 개발 중입니다.')}
                                  className="w-full text-gray-400 cursor-not-allowed border-l-2 border-transparent group flex items-center justify-between px-3 py-2 text-sm font-medium"
                                >
                                  <div className="flex items-center">
                                    <item.icon className="h-4 w-4 mr-2" />
                                    {item.name}
                                  </div>
                                  <span className="bg-gray-200 text-gray-500 text-xs rounded-full px-2 py-0.5">
                                    준비중
                                  </span>
                                </button>
                              );
                            }

                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setIsSidebarOpen(false)}
                                className={`${
                                  isCurrentPath
                                    ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-700'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-2 border-transparent'
                                } group flex items-center justify-between px-3 py-2 text-sm font-medium transition-all`}
                              >
                                <div className="flex items-center">
                                  <item.icon className="h-4 w-4 mr-2" />
                                  {item.name}
                                </div>
                                {item.badge && (
                                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                                    {item.badge}
                                  </span>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>

              {/* 하단 메뉴 - 고정 */}
              <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
                <button
                  className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors w-full text-left"
                  onClick={handleLogout}
                >
                  <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-3" />
                  로그아웃
                </button>
              </div>
            </div>
          </>
        )}

        {/* 데스크톱 사이드바 */}
        <div
          className={`hidden lg:block fixed inset-y-0 left-0 ${
            isDesktopSidebarCollapsed ? 'w-16' : 'w-64'
          } bg-white shadow-lg flex flex-col h-screen transform transition-all duration-300 ease-in-out flex-shrink-0`}
        >
          {/* 헤더 부분 - 고정 */}
          <div className="flex-shrink-0">
            <div
              className={`${
                isDesktopSidebarCollapsed ? 'p-2' : 'p-6'
              } border-b border-gray-200`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BuildingOfficeIcon
                    className={`${
                      isDesktopSidebarCollapsed ? 'h-6 w-6' : 'h-8 w-8'
                    } text-blue-600`}
                  />
                  {!isDesktopSidebarCollapsed && (
                    <div className="ml-3">
                      <h1 className="text-xl font-bold text-gray-900">
                        {organizationName}
                      </h1>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 데스크톱 사이드바 토글 버튼 */}
            <div className="hidden lg:block p-2 border-b border-gray-200">
              <button
                onClick={toggleDesktopSidebar}
                className="w-full flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                title={
                  isDesktopSidebarCollapsed
                    ? '사이드바 펼치기'
                    : '사이드바 접기'
                }
              >
                {isDesktopSidebarCollapsed ? (
                  <ChevronDoubleRightIcon className="h-5 w-5" />
                ) : (
                  <ChevronDoubleLeftIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* 알림 센터 - 데스크톱에서만 표시 */}
            {!isDesktopSidebarCollapsed && (
              <div className="hidden lg:block px-4 py-3 border-b border-gray-200">
                <Link
                  to="/owner/notifications"
                  onClick={() => setIsSidebarOpen(false)}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <BellIcon className="h-5 w-5 text-gray-600" />
                    <span className="ml-2 text-sm text-gray-700">알림</span>
                  </div>
                  {totalNotifications > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                      {totalNotifications > 99 ? '99+' : totalNotifications}
                    </span>
                  )}
                </Link>
              </div>
            )}
          </div>

          {/* 네비게이션 - 스크롤 가능 영역 */}
          <nav
            className={`flex-1 overflow-y-auto mt-6 ${
              isDesktopSidebarCollapsed ? 'px-2' : 'px-4'
            } pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100`}
            style={{ maxHeight: 'calc(100vh - 300px)' }}
          >
            {isDesktopSidebarCollapsed ? (
              // 접힌 상태: 아이콘만 표시
              navigationCategories.map((category) => (
                <div key={category.title} className="mb-4">
                  {category.items.map((item) => {
                    const isCurrentPath = isActive(item.href);

                    if (item.comingSoon) {
                      return (
                        <button
                          key={item.name}
                          onClick={() => alert('🚧 준비중입니다\n\n이 기능은 현재 개발 중입니다.')}
                          className="w-full text-gray-400 cursor-not-allowed group flex items-center justify-center p-2 rounded-lg transition-all relative"
                          title={`${item.name} (준비중)`}
                        >
                          <item.icon className="h-5 w-5" />
                          <span className="absolute -top-1 -right-1 bg-gray-200 text-gray-500 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            !
                          </span>
                        </button>
                      );
                    }

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsSidebarOpen(false)}
                        className={`${
                          isCurrentPath
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        } group flex items-center justify-center p-2 rounded-lg transition-all relative`}
                        title={item.name}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.badge && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              ))
            ) : (
              // 펼친 상태: 전체 메뉴 표시
              navigationCategories.map((category) => {
                const isExpanded = expandedCategories.includes(category.title);
                return (
                  <div key={category.title} className="mb-4">
                    <button
                      onClick={() => toggleCategory(category.title)}
                      className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                    >
                      <div className="flex items-center">
                        <category.icon className="h-5 w-5 mr-2 text-gray-500" />
                        {category.title}
                      </div>
                      <span className="flex items-center gap-1.5">
                        {!isExpanded && categoryBadge(category) > 0 && (
                          <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{categoryBadge(category)}</span>
                        )}
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="mt-1 space-y-1 pl-7">
                        {category.items.map((item) => {
                          const isCurrentPath = isActive(item.href);

                          if (item.comingSoon) {
                            return (
                              <button
                                key={item.name}
                                onClick={() => alert('🚧 준비중입니다\n\n이 기능은 현재 개발 중입니다.')}
                                className="w-full text-gray-400 cursor-not-allowed border-l-2 border-transparent group flex items-center justify-between px-3 py-2 text-sm font-medium"
                              >
                                <div className="flex items-center">
                                  <item.icon className="h-4 w-4 mr-2" />
                                  {item.name}
                                </div>
                                <span className="bg-gray-200 text-gray-500 text-xs rounded-full px-2 py-0.5">
                                  준비중
                                </span>
                              </button>
                            );
                          }

                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              onClick={() => setIsSidebarOpen(false)}
                              className={`${
                                isCurrentPath
                                  ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-2 border-transparent'
                              } group flex items-center justify-between px-3 py-2 text-sm font-medium transition-all`}
                            >
                              <div className="flex items-center">
                                <item.icon className="h-4 w-4 mr-2" />
                                {item.name}
                              </div>
                              {item.badge && (
                                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                                  {item.badge}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </nav>

          {/* 하단 메뉴 - 고정 */}
          <div
            className={`flex-shrink-0 ${
              isDesktopSidebarCollapsed ? 'p-2' : 'p-4'
            } border-t border-gray-200 bg-white`}
          >
            <button
              className={`text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center ${
                isDesktopSidebarCollapsed ? 'justify-center p-2' : 'px-2 py-2'
              } text-sm font-medium rounded-md transition-colors w-full ${
                isDesktopSidebarCollapsed ? '' : 'text-left'
              }`}
              onClick={handleLogout}
              title={isDesktopSidebarCollapsed ? '로그아웃' : undefined}
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              {!isDesktopSidebarCollapsed && (
                <span className="ml-3">로그아웃</span>
              )}
            </button>
          </div>
        </div>

        {/* 메인 콘텐츠 영역 */}
        <div
          className={`flex-1 overflow-y-auto pt-16 lg:pt-0 min-h-screen ${
            isDesktopSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
          } transition-all duration-300 ease-in-out`}
        >
          {/* 빨간날·일요일·주요 일정에만 뜨는 알림 띠 (평소엔 안 보임) */}
          <TodayNoticeBanner />
          <Outlet />
        </div>
      </div>

      {/* 운영 도우미 챗봇 (RAG 기반 매뉴얼 Q&A) */}
      <ChatWidget />
    </>
  );
};

export default OwnerLayoutV2;
